import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, LabelWithInteractions, Paragraph } from '../../shared/components';
import { Grid, Theme, styled, useMediaQuery, useTheme } from '@material-ui/core';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';

type SupportContentItemProps = {
  id: string;
  primary?: boolean;
  onClick: () => void;
};

type PRIMARY_IDS = 'message' | 'meeting' | 'call';

const TitleWrapper = styled('div')<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const DescriptionWrapper = styled('div')<Theme>(({ theme }) => ({
  minHeight: 'auto',
  [theme.breakpoints.up(theme.breakpoints.values.md)]: {
    minHeight: '9rem',
  },
}));

const Title = styled('h3')<Theme>(({ theme }) => ({
  fontSize: theme.typography.h3.fontSize,
  display: 'flex',
  gap: theme.spacing(1),
}));

const SubTitle = styled('div')<Theme>(({ theme }) => ({
  fontSize: theme.typography.h4.fontSize,
  color: theme.palette.grey['700'],
}));

const StyledGridItem = styled(Grid)<Theme>(({ theme }) => ({
  '&:not(:last-child) > div': {
    [theme.breakpoints.up(theme.breakpoints.values.md)]: {
      borderRight: `0.1rem solid ${theme.palette.grey['200']}`,
      paddingRight: theme.spacing(3),
    },
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
      borderBottom: `0.1rem solid ${theme.palette.grey['200']}`,
      borderTop: `0.1rem solid ${theme.palette.grey['200']}`,
      paddingBottom: theme.spacing(3),
    },
  },
}));

export const SupportContentItem: FC<SupportContentItemProps> = ({
  id,
  primary = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.md));

  return (
    <StyledGridItem item xs={12} md={primary ? 4 : 6}>
      <div>
        <TitleWrapper>
          <Title>
            {primary && (
              <FontAwesomeSpriteIcon
                icon={
                  {
                    message: SpriteIconsLight.CONTACT,
                    meeting: SpriteIconsLight.USER,
                    call: SpriteIconsLight.INFO,
                  }[id as PRIMARY_IDS]
                }
                iconSize="lg"
              />
            )}
            {t(`support.${id}.title`)}
          </Title>
          {primary && <SubTitle>{t(`support.${id}.subTitle`)}</SubTitle>}
        </TitleWrapper>
        <DescriptionWrapper>
          {id === 'call' ? (
            <LabelWithInteractions
              label={t(`support.call.description`)}
              interactions={[
                {
                  type: 'tooltip',
                  data: {
                    label: 'de kantooruren',
                    content: t('support.call.openingHours'),
                  },
                },
              ]}
              options={{
                linkUnderline: 'always',
                tooltipPlacement: 'bottom',
              }}
            />
          ) : (
            <Paragraph>
              {/* TODO - NEEDS TRANSLATION */}
              {id === 'manual' &&
                "Bekijk de startgids en instructievideo's om alles uit Lernova te halen."}
              {id !== 'manual' && t(`support.${id}.description`)}
            </Paragraph>
          )}
        </DescriptionWrapper>
        {primary ? (
          <Button fullWidth={isDesktop} onClick={onClick}>
            {t(`support.${id}.button`)}
          </Button>
        ) : (
          <Button variant="secondary" onClick={onClick}>
            {t(`support.${id}.button`)}
          </Button>
        )}
      </div>
    </StyledGridItem>
  );
};
